import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import flowRuleApi from '@/api/config/flowRuleApi'
import { queryKeys } from '@/constant/common'
import { useUnits } from '@/hooks/useUnits'
import useTranslation from '@/hooks/useTranslation'
import useSearchFlowRuleId from '@/features/config/hooks/hydraulic/flowRule/useSearchFlowRuleId'
import DeviationBandSlider from './DeviationBandSlider'
import InfoTip from './InfoTip'
import { DANGER_TEXT, flowRuleState } from './constants'
import { useRuleCopy } from './use-rule-copy'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'

interface FlowAlarmDialogProps {
  open: boolean
  /**
   * Where the Flow Alarm chip was when clicked — the popup opens from it.
   * `left` is the chip's RIGHT edge: the panel hangs leftwards from there (see
   * `transformOrigin`), because these chips sit far enough right that a panel
   * opening rightwards ran off the screen.
   */
  anchorPosition: { top: number; left: number }
  interfaceId: number
  pipeId: number
  pipeName?: string
  /** Metric l/m; drives the worked example. */
  pipeCapacity?: number
  flowRuleId: number
  isViewOnly?: boolean
  onClose: () => void
  /** Runs after a successful save so the pipe list can refresh its chips. */
  onSaved?: () => void
}

type Draft = Record<string, number | boolean>

const PANEL_BG = '#0e2b27'
const PANEL_INK = '#eef7f4'
const PANEL_MUTED = '#8fb0a9'
const OK_GREEN = '#5fe0ad'

// Switch chrome for the dark face: the default unchecked track (black at 38%
// opacity) disappears against the teal panel, so the off state gets a visible
// light track and thumb of its own.
const faceSwitchSx = (onColor: string) => ({
  '& .MuiSwitch-switchBase': { color: '#dfe9e6' },
  '& .MuiSwitch-track': {
    backgroundColor: 'rgba(223, 233, 230, 0.35)',
    opacity: 1,
  },
  '& .MuiSwitch-switchBase.Mui-checked': { color: onColor },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: onColor,
    opacity: 1,
  },
  '& .MuiSwitch-switchBase.Mui-disabled': { color: '#9fb3ae' },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
    backgroundColor: 'rgba(223, 233, 230, 0.2)',
    opacity: 1,
  },
})

const NUMERIC_FIELDS = [
  'allowedIdleFlow',
  'maxLowDeviation',
  'maxHighDeviation',
  'startDelay',
  'changeDelay',
  'errorDelay',
] as const

/**
 * The flow alarm as a control panel: a dark instrument face with the live
 * readings on top, the master switch as the headline control, and the
 * thresholds and timing beneath it. Opens from the pipe row's Flow Alarm chip.
 */
const FlowAlarmDialog = ({
  open,
  anchorPosition,
  interfaceId,
  pipeId,
  pipeName,
  pipeCapacity,
  flowRuleId,
  isViewOnly,
  onClose,
  onSaved,
}: FlowAlarmDialogProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  const isDark = useSelector(selectTheme) === mode_type.dark
  // The band labels sit on a 14% tint of their own colour. Composited over the
  // dark panel that leaves the light-mode red at 3.44:1 and the green at
  // 2.72:1, so both are lightened for dark.
  const bandBad = isDark ? '#f87171' : DANGER_TEXT
  const bandOk = isDark ? '#4ade80' : '#2f7d54'
  const queryClient = useQueryClient()
  const { convertLabelUnit, convertFromMetricV2, convertToMetricV2 } =
    useUnits()

  const { data: rule, isLoading } = useSearchFlowRuleId(interfaceId, flowRuleId)

  const [draft, setDraft] = useState<Draft>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!rule || !open) return
    setDraft({
      active: Boolean(rule.active),
      autoAction: Boolean(rule.autoAction),
      allowedIdleFlow: convertFromMetricV2(
        Number(rule.allowedIdleFlow ?? 0),
        'l/m',
        'gpm',
        2,
      ),
      maxLowDeviation: Number(rule.maxLowDeviation ?? 0),
      maxHighDeviation: Number(rule.maxHighDeviation ?? 0),
      startDelay: Number(rule.startDelay ?? 0),
      changeDelay: Number(rule.changeDelay ?? 0),
      errorDelay: Number(rule.errorDelay ?? 0),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.id, rule, open])

  const dirty =
    !!rule &&
    (NUMERIC_FIELDS.some(
      (field) =>
        String(draft[field] ?? '') !==
        String(
          field === 'allowedIdleFlow'
            ? convertFromMetricV2(
                Number(rule.allowedIdleFlow ?? 0),
                'l/m',
                'gpm',
                2,
              )
            : Number((rule as any)[field] ?? 0),
        ),
    ) ||
      Boolean(draft.active) !== Boolean(rule.active) ||
      Boolean(draft.autoAction) !== Boolean(rule.autoAction))

  const set = (field: string) => (value: number | boolean) =>
    setDraft((prev) => ({ ...prev, [field]: value }))

  const handleSave = async () => {
    if (!rule) return
    setSaving(true)
    try {
      await flowRuleApi.update({
        id: rule.id,
        interfaceId: rule.interfaceId,
        pipeId,
        active: Boolean(draft.active),
        autoAction: Boolean(draft.autoAction),
        allowedIdleFlow: convertToMetricV2(
          Number(draft.allowedIdleFlow ?? 0),
          'l/m',
          'gpm',
          2,
        ),
        maxLowDeviation: Number(draft.maxLowDeviation ?? 0),
        maxHighDeviation: Number(draft.maxHighDeviation ?? 0),
        startDelay: Number(draft.startDelay ?? 0),
        changeDelay: Number(draft.changeDelay ?? 0),
        errorDelay: Number(draft.errorDelay ?? 0),
        // The API replaces the record, so the learning trio rides along
        // unchanged from the loaded rule.
        stableDeviation: Number(rule.stableDeviation ?? 0),
        stableCount: Number(rule.stableCount ?? 0),
        stableMax: Number(rule.stableMax ?? 0),
      })
      queryClient.invalidateQueries([queryKeys.flowRule])
      onSaved?.()
      onClose()
    } catch (error: any) {
      toast.error(error?.response?.data?.Message ?? copy.saveAlarmFailed)
    } finally {
      setSaving(false)
    }
  }

  const state = flowRuleState(rule?.state)
  const flowUnit = convertLabelUnit('l/m')
  const armed = Boolean(draft.active)

  const stateColor =
    state.tone === 'ok'
      ? OK_GREEN
      : state.tone === 'bad'
        ? '#ff8f8a'
        : '#74bde0'

  const field = (
    name: (typeof NUMERIC_FIELDS)[number],
    label: string,
    unit: string,
    tip?: string,
  ) => (
    <Stack sx={{ gap: '5px', minWidth: 0 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: '4px' }}>
        <Typography
          sx={{ fontSize: '12px', fontWeight: 650, color: 'grey.800' }}
        >
          {label}{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'grey.600' }}>
            {unit}
          </Box>
        </Typography>
        {tip ? <InfoTip text={tip} /> : null}
      </Stack>
      <TextField
        size="small"
        value={draft[name] ?? ''}
        disabled={isViewOnly}
        onChange={(event) => set(name)(Number(event.target.value))}
        inputProps={{ inputMode: 'decimal', style: { padding: '8px 10px' } }}
        sx={{
          // White field, visible border, hover feedback: the tinted grey fill
          // used before read as disabled.
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 600,
            backgroundColor: 'background.default',
            '& fieldset': { borderColor: 'border.main' },
            '&:hover fieldset': { borderColor: 'rgba(0, 71, 68, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
        }}
      />
    </Stack>
  )

  // A worked example at half the pipe's capacity: the same arithmetic the
  // controller runs, so the sliders' effect is visible as real numbers.
  const exampleExpected = convertFromMetricV2(
    Number(pipeCapacity ?? 0) / 2,
    'l/m',
    'gpm',
    2,
  )
  const lowPct = Number(draft.maxLowDeviation ?? 0)
  const highPct = Number(draft.maxHighDeviation ?? 0)
  const round1 = (n: number) => Math.round(n * 10) / 10
  // The pure band, without the idle offset: the example's job is to translate
  // the slider above it, so the two must mirror each other exactly — the idle
  // allowance shifting both bounds made them look reversed. Idle's role in the
  // arithmetic stays in the tooltip.
  const exampleLowBound = round1(
    Math.max(0, exampleExpected * (1 - lowPct / 100)),
  )
  const exampleHighBound = round1(exampleExpected * (1 + highPct / 100))

  // Drawn, not tabulated: the same green-band-in-red language as the slider
  // above, but on the absolute scale of this pipe — so the percentages turn
  // into litres in one glance.
  const exampleScaleEnd = Math.max(
    convertFromMetricV2(Number(pipeCapacity ?? 0), 'l/m', 'gpm', 2),
    exampleHighBound * 1.15,
    1,
  )
  const pct = (v: number) =>
    Math.min(100, Math.max(0, (v / exampleScaleEnd) * 100))
  const lowNever = lowPct >= 100
  const highNever = highPct >= 100
  const zoneLowW = lowNever ? 0 : pct(exampleLowBound)
  const zoneHighStart = highNever ? 100 : pct(exampleHighBound)

  const sectionTitle = (label: string, tip?: string) => (
    <Stack direction="row" sx={{ alignItems: 'center', gap: '5px' }}>
      <Typography
        sx={{
          fontSize: '11.5px',
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'grey.800',
        }}
      >
        {label}
      </Typography>
      {tip ? <InfoTip text={tip} /> : null}
    </Stack>
  )

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={() => !saving && onClose()}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: '6px',
            borderRadius: '12px',
            width: '560px',
            maxWidth: 'calc(100vw - 32px)',
            overflow: 'hidden',
            boxShadow:
              '0 6px 16px rgba(16, 24, 40, 0.16), 0 20px 44px rgba(16, 24, 40, 0.18)',
          },
        },
      }}
    >
      {/* ---- instrument face: identity, live state, master switch ---- */}
      <Box sx={{ backgroundColor: PANEL_BG, p: '16px 18px 14px' }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', gap: '8px' }}>
          <Stack sx={{ gap: '1px', flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                // Matches FlowSectionPopover: the section title reads at full
                // strength, the pipe name below is the context.
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: PANEL_INK,
              }}
            >
              {copy.flowAlarm}
            </Typography>
            <Stack
              direction="row"
              sx={{ alignItems: 'baseline', gap: '8px', minWidth: 0 }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 680,
                  color: PANEL_INK,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {pipeName || `Pipe ${pipeId}`}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11.5px',
                  fontWeight: 650,
                  color: stateColor,
                  flexShrink: 0,
                }}
              >
                {state.labelKey ? t(state.labelKey) : '—'}
              </Typography>
            </Stack>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={{ color: PANEL_MUTED, mt: '-4px', mr: '-6px' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {isLoading || !rule ? (
          <Stack sx={{ alignItems: 'center', py: '14px' }}>
            <CircularProgress size={20} sx={{ color: PANEL_MUTED }} />
          </Stack>
        ) : (
          <Stack sx={{ pt: '10px', gap: '2px' }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: '6px' }}>
              <Switch
                checked={armed}
                disabled={isViewOnly}
                onChange={(event) => set('active')(event.target.checked)}
                inputProps={{ 'aria-label': copy.flowAlarm }}
                sx={faceSwitchSx(OK_GREEN)}
              />
              <Typography
                sx={{ fontSize: '14px', fontWeight: 680, color: PANEL_INK }}
              >
                {armed ? copy.alarmActivated : copy.alarmOff}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: '6px',
                opacity: armed ? 1 : 0.45,
              }}
            >
              <Switch
                checked={Boolean(draft.autoAction)}
                disabled={isViewOnly || !armed}
                onChange={(event) => set('autoAction')(event.target.checked)}
                inputProps={{ 'aria-label': copy.automaticAction }}
                sx={faceSwitchSx('#f2b24c')}
              />
              <Typography
                sx={{
                  fontSize: '13.5px',
                  fontWeight: 600,
                  color: PANEL_INK,
                  flex: '0 1 auto',
                }}
              >
                {copy.automaticAction}
              </Typography>
              <InfoTip text={copy.tips.automaticAction} placement="right" />
            </Stack>
          </Stack>
        )}
      </Box>

      {/* ---- controls ---- */}
      {rule && !isLoading && (
        <Box
          sx={{
            backgroundColor: 'background.default',
            width: '100%',
            minWidth: 0,
          }}
        >
          {!armed ? (
            <Typography
              sx={{
                m: '16px 18px',
                fontSize: '12px',
                color: 'grey.600',
                px: '11px',
                py: '11px',
                borderRadius: '8px',
                border: '1px dashed',
                borderColor: 'border.main',
                backgroundColor: 'background.overrideDefault',
              }}
            >
              {copy.alarmDisabled}
            </Typography>
          ) : (
            <Stack sx={{ p: '16px 18px 4px', gap: '15px' }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: '12px',
                  px: '12px',
                  py: '10px',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'rgba(0, 71, 68, 0.35)',
                  backgroundColor: 'rgba(0, 71, 68, 0.07)',
                }}
              >
                <Stack sx={{ flex: 1, gap: '1px' }}>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: '5px' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '13.5px',
                        fontWeight: 680,
                        color: 'text.main',
                      }}
                    >
                      {copy.idleFlowSection}
                    </Typography>
                    <InfoTip text={copy.tips.idleFlow} />
                  </Stack>
                  <Typography sx={{ fontSize: '11.5px', color: 'grey.600' }}>
                    background flow tolerated while nothing runs
                  </Typography>
                </Stack>
                <TextField
                  size="small"
                  value={draft.allowedIdleFlow ?? ''}
                  disabled={isViewOnly}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      allowedIdleFlow: Number(event.target.value),
                    }))
                  }
                  inputProps={{
                    inputMode: 'decimal',
                    style: {
                      padding: '8px 10px',
                      fontSize: '15px',
                      fontWeight: 700,
                      textAlign: 'right',
                      width: '64px',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      backgroundColor: 'background.default',
                      '& fieldset': { borderColor: 'border.main' },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 71, 68, 0.5)',
                      },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '12.5px',
                    fontWeight: 650,
                    color: 'grey.800',
                  }}
                >
                  {flowUnit}
                </Typography>
              </Stack>

              <Stack sx={{ gap: '4px' }}>
                {sectionTitle(copy.deviationBand, copy.tips.deviationBand)}
                <DeviationBandSlider
                  low={Number(draft.maxLowDeviation ?? 0)}
                  high={Number(draft.maxHighDeviation ?? 0)}
                  disabled={isViewOnly}
                  onChange={(lowValue, highValue) =>
                    setDraft((prev) => ({
                      ...prev,
                      maxLowDeviation: lowValue,
                      maxHighDeviation: highValue,
                    }))
                  }
                />
              </Stack>

              {exampleExpected > 0 && (
                <Stack sx={{ gap: '4px', mt: '10px' }}>
                  <Typography
                    sx={{
                      fontSize: '12.5px',
                      fontWeight: 680,
                      color: 'text.main',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {copy.example}:
                    </Box>{' '}
                    with {round1(exampleExpected)} {flowUnit} expected flow
                  </Typography>

                  <Box sx={{ px: '10px', pt: '16px' }}>
                    {/* the pipe's absolute scale, painted with this alarm's zones */}
                    <Box
                      sx={{
                        position: 'relative',
                        height: '30px',
                        borderRadius: '7px',
                        overflow: 'hidden',
                        display: 'flex',
                        border: '1px solid',
                        borderColor: 'border.main',
                      }}
                    >
                      {zoneLowW > 0 && (
                        <Box
                          sx={{
                            width: `${zoneLowW}%`,
                            backgroundColor: 'rgba(220, 53, 69, 0.14)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {zoneLowW > 16 && (
                            <Typography
                              sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                color: bandBad,
                              }}
                            >
                              TOO LOW
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: `${zoneHighStart - zoneLowW}%`,
                          backgroundColor: 'rgba(47, 125, 84, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: bandOk,
                          }}
                        >
                          OK
                        </Typography>
                      </Box>
                      {zoneHighStart < 100 && (
                        <Box
                          sx={{
                            width: `${100 - zoneHighStart}%`,
                            backgroundColor: 'rgba(220, 53, 69, 0.14)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {100 - zoneHighStart > 16 && (
                            <Typography
                              sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                color: bandBad,
                              }}
                            >
                              TOO HIGH
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* expected flow marker */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${pct(exampleExpected)}%`,
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          backgroundColor: 'grey.800',
                        }}
                      />
                    </Box>

                    {/* boundary numbers, anchored to their zone edges */}
                    <Box
                      sx={{ position: 'relative', height: '16px', mt: '3px' }}
                    >
                      <Typography
                        sx={{
                          position: 'absolute',
                          left: 0,
                          fontSize: '10px',
                          fontFamily: 'monospace',
                          color: 'grey.600',
                        }}
                      >
                        0
                      </Typography>
                      {!lowNever && (
                        <Typography
                          sx={{
                            position: 'absolute',
                            left: `${zoneLowW}%`,
                            transform: 'translateX(-50%)',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: 'text.main',
                          }}
                        >
                          {exampleLowBound}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          position: 'absolute',
                          left: `${pct(exampleExpected)}%`,
                          transform: 'translateX(-50%)',
                          fontSize: '10.5px',
                          fontWeight: 650,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: 'grey.800',
                          top: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Expected Flow
                      </Typography>
                      {!highNever && (
                        <Typography
                          sx={{
                            position: 'absolute',
                            left: `${zoneHighStart}%`,
                            transform: 'translateX(-50%)',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: 'text.main',
                          }}
                        >
                          {exampleHighBound}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          position: 'absolute',
                          right: 0,
                          fontSize: '10px',
                          fontFamily: 'monospace',
                          color: 'grey.600',
                        }}
                      >
                        {round1(exampleScaleEnd)} {flowUnit}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              )}

              <Stack sx={{ gap: '8px' }}>
                {sectionTitle(copy.timing)}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '9px',
                  }}
                >
                  {field(
                    'startDelay',
                    copy.startDelayLabel,
                    '(s)',
                    copy.tips.startDelay,
                  )}
                  {field(
                    'changeDelay',
                    copy.changeDelayLabel,
                    '(s)',
                    copy.tips.changeDelay,
                  )}
                  {field(
                    'errorDelay',
                    copy.errorDelayLabel,
                    '(s)',
                    copy.tips.errorDelay,
                  )}
                </Box>
              </Stack>
            </Stack>
          )}

          <Stack
            direction="row"
            sx={{
              gap: '8px',
              px: '18px',
              py: '13px',
              borderTop: '1px solid',
              borderColor: 'border.main',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="secondary"
              size="small"
              disabled={saving}
              onClick={onClose}
            >
              {t('commonActionsCancel')}
            </Button>
            {!isViewOnly && (
              <Button
                variant="primary"
                size="small"
                disabled={saving || !dirty}
                onClick={handleSave}
              >
                {saving ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : (
                  t('commonActionsSave')
                )}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Popover>
  )
}

export default FlowAlarmDialog
